import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'components/InfiniteScroll';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { InfiniteScrollRef } from 'components/InfiniteScroll/InfiniteScroll.component';
import ChatMessage from './components/ChatMessage';
import { MessageType, MessageLocation, MESSAGE_LOCATION } from 'models/Message';
import {
  getCanManageReleaseOrder,
  getSessionLead,
  setDatesToMessages,
} from './helpers';
import useChatListener from './hooks/useChatListeners';
import useChatScroll from './hooks/useChatScroll';
import useCallbackRef from 'hooks/useCallbackRef';
import bus, { CallbackBusType, StandardEventTypes } from 'modules/bus';
import uniqBy from 'lodash/uniqBy';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ContentLoader, { IContentLoaderProps } from 'react-content-loader';
import api from 'api';
import {
  getHasElementsInArray,
  getIsArrayEmpty,
  getMemberInitials,
} from 'utils';
import LockclosedIcon from 'icons/Lockclosed.icon';
import { MessageForm } from 'components/MessageForm';
import useUploadMessageFile from 'hooks/useUploadMessageFile';
import MessagesEmptyState from './components/MessagesEmptyState';
import { AxiosRequestConfig } from 'axios';
import useBusEvent from 'hooks/useBusEvent';
import MessagesEmptyImage from 'assets/images/market-a-listing.webp';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import {
  MessageCreatedEventData,
  MessageDeletedEventData,
  MessageSeenEventData,
} from 'services/socketService/socketEventData';
import { trimRichTextValueFromJson } from 'components/RichTextEditorField/utils';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import { SIZE } from 'models/Size';
import useMessageRepresented from '../../hooks/useMessageRepresented';
import useMarkSessionRead from '../../hooks/useMarkSessionRead';
import WarningBanner from 'components/WarningBanner';
import useClosedChat from '../../hooks/useClosedChat';
import {
  FocusTrigger,
  getSuggestionAction,
} from 'components/MessageForm/utils';
import useReplySuggestions from './hooks/useReplySuggestions';
import ReleaseOrderPausedBanner from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/components/ManageLeadDrawer/components/ReleaseOrderPausedBanner';
import { Lead } from 'models/Lead';
import useWithdrawLead from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/hooks/useWithdrawLead';
import { useQueryClient } from '@tanstack/react-query';
import { RELEASE_ORDER_STATUS_QUERY_KEY } from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/components/ManageLeadDrawer/components/ReleaseOrderPausedBanner/hooks/useReleaseOrderStatusQuery';
import { getPausedLeadMixpanelProps } from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/components/ManageLeadDrawer/components/ReleaseOrderPausedBanner/utils';
import { PAUSED_LEAD_LOCATION } from 'models/MixpanelEvents';

import './Chat.styles.scss';
import './Chat.styles.responsive.scss';

export const UserPhotoPlaceholder: React.FC<IContentLoaderProps> = (props) => (
  <ContentLoader
    style={{ width: '120px', height: '69px' }}
    backgroundColor="#e7f1fc"
    foregroundColor="#ffffff"
    preserveAspectRatio="none"
    {...props}
  >
    <circle cx="60" cy="20" r="20" />;
    <rect x="0" y="48" rx="8" ry="8" width="120px" height="16" />
  </ContentLoader>
);

type ChatProps = {
  focusTrigger?: FocusTrigger;
  messageLocation: MessageLocation;
  showUserData?: boolean;
  showButtons?: boolean;
};

const Chat: React.FC<ChatProps> = (props) => {
  const {
    focusTrigger,
    messageLocation,
    showUserData = false,
    showButtons = false,
  } = props;

  const {
    currentSession,
    loadingSession,
    sessionId,
    setContactOpen,
    setContactsOpen,
    updateCurrentSession,
  } = useContext(MessagingContext);

  const {
    isDirectMessage,
    interlocutor,
    isGroupChat,
    isRepresentingSellSide,
    buyRepresentatives,
    leadId,
  } = currentSession || {};

  const canManageReleaseOrder = getCanManageReleaseOrder(
    currentSession,
    messageLocation,
  );

  const withdrawLead = useCallback(
    async (lead: Lead, revokeDataRoomAccess: boolean) => {
      await api.portfolio.seller.withdrawLead(lead._id, revokeDataRoomAccess);

      updateCurrentSession({ leadWithdrawnInterest: true });

      return true;
    },
    [updateCurrentSession],
  );

  const { withdrawLeadModal, openWithdrawModal } =
    useWithdrawLead(withdrawLead);

  const queryClient = useQueryClient();

  // person on the other side of a 1-on-1 chat (direct message or deal chat)
  const oneOnOneRecipient = useMemo(
    () =>
      isDirectMessage
        ? interlocutor
        : !isGroupChat && isRepresentingSellSide
        ? buyRepresentatives?.[0]
        : undefined,
    [
      isDirectMessage,
      interlocutor,
      isGroupChat,
      isRepresentingSellSide,
      buyRepresentatives,
    ],
  );

  const hasRecipientWithoutAccount =
    !!oneOnOneRecipient && !oneOnOneRecipient.hasAccessedPlatform;

  const classes = classNames('chat');

  const onUploadReq = useCallback(
    (file: File, config: AxiosRequestConfig) =>
      api.messages.uploadFile(sessionId, file, config),
    [sessionId],
  );

  const {
    files,
    uploadedFiles,
    areAllFilesProcessed,
    restRootProps,
    clearFiles,
    onFileRemove,
    onFileUpload,
    getInputProps,
  } = useUploadMessageFile({
    onUploadReq,
  });

  const [hasNewMessage, setHasNewMessage] = useState<boolean>(false);

  const [messagesContainer, messagesContainerRef] =
    useCallbackRef<InfiniteScrollRef>();

  const { forbidMessaging, bannerText, forbidMessagingText } =
    useClosedChat(currentSession);

  const { dealName, dealLogo, representerName, representerLogo } =
    useMessageRepresented()(currentSession);

  const markSessionRead = useMarkSessionRead();

  const {
    loading,
    items: messages,
    setItems: setMessages,
    onContainerScrolled: onMessagesScroll,
  } = useInfinitePagination<MessageType>({
    condition: !!sessionId,
    resetItemsWhenReset: false,
    resetDeps: [sessionId],
    dependencies: [sessionId],
    makeRequest: async (currentPage: number) => {
      return api.messages
        .getSessionMessages(
          {
            $page: currentPage.toString(),
            $limit: '50',
            $sort: '-createdAt',
          },
          sessionId,
        )
        .then((res) => res.data);
    },
    onItemsLoad: (cp) => {
      // when it is scrollable and current page not first one
      // then scroll to bottom to see loaded messages
      if (shouldScrollTL && cp === 1) scrollToView();
    },
  });

  const finalMessages = useMemo(
    () =>
      setDatesToMessages(messages).filter((msg) => msg.sessionId === sessionId),
    [messages, sessionId],
  );

  const { shouldScrollTL, scrollToBottom } = useChatScroll({
    condition: hasNewMessage,
    container: messagesContainer?.container as HTMLDivElement,
  });

  const isSessionReady = !loading && currentSession?._id === sessionId;

  const {
    suggestions: replySuggestions,
    loading: loadingReplySuggestions,
    removeSuggestions: removeReplySuggestions,
    suggestionsShown,
    selectedSuggestion,
    selectSuggestion: selectReplySuggestion,
  } = useReplySuggestions({
    sessionId,
    lastMessage: isSessionReady ? finalMessages[0] : undefined,
    disabled: forbidMessaging,
  });

  // The Data Room member chat drawer renders <Chat> without a wrapper that
  // marks the session read on open (unlike the Messaging page and lead drawer),
  // so do it here — but only when the session actually has unread messages, to
  // avoid a needless read request on every open. Clears the unread dot on the
  // Members page + header.
  useEffect(() => {
    if (messageLocation !== MESSAGE_LOCATION.DATA_ROOM) return;
    if (!sessionId || currentSession?._id !== sessionId) return;
    if (!currentSession?.hasUnreadMessages) return;

    markSessionRead(
      sessionId,
      currentSession?.isExternalCommunication ?? false,
    );
  }, [messageLocation, sessionId, currentSession, markSessionRead]);

  const loadingSpinner = useMemo(
    () => (isSessionReady ? null : <Loader overlay={false} />),
    [isSessionReady],
  );

  const onMessageArrived = useCallback(
    ({ message, session }: MessageCreatedEventData) => {
      if (sessionId !== message.sessionId) return;

      if (canManageReleaseOrder)
        queryClient.invalidateQueries({
          queryKey: [RELEASE_ORDER_STATUS_QUERY_KEY, { leadId }],
        });

      // if user scrolled container above last message
      // and new message arrived then set flag for showSTL(show scroll to latest)
      if (shouldScrollTL) {
        setHasNewMessage(true);
        markSessionRead(sessionId, session.isExternalCommunication);
      }
    },
    [
      sessionId,
      shouldScrollTL,
      canManageReleaseOrder,
      queryClient,
      leadId,
      markSessionRead,
    ],
  );

  useChatListener({
    setMessages,
    onMessageArrived,
  });

  const scrollToView = useCallback(() => {
    setHasNewMessage(false);
    scrollToBottom();
  }, [scrollToBottom]);

  const { t } = useTranslation();

  const handleOnSendMessage = useCallback(
    async (message: string) => {
      const trimmedMessage = trimRichTextValueFromJson(message);

      const { data: sentMessage } = await api.messages.sendMesssage(
        sessionId,
        trimmedMessage,
        uploadedFiles,
        messageLocation,
        {
          suggestionsShown,
          suggestionAction: getSuggestionAction(
            trimmedMessage,
            selectedSuggestion,
          ),
        },
      );

      // dedup in case the socket echo of this message already arrived
      setMessages((oldMessages) =>
        uniqBy([sentMessage, ...oldMessages], '_id'),
      );

      const { createdChatSessionFiles } = sentMessage;

      clearFiles();

      // event message that has been sent in current session
      if (shouldScrollTL) scrollToView();
      bus.broadcastEvent(StandardEventTypes.MESSAGE_SENT, sentMessage);

      if (getHasElementsInArray(createdChatSessionFiles))
        bus.broadcastEvent(
          StandardEventTypes.FILES_SENT,
          createdChatSessionFiles,
        );
    },
    [
      sessionId,
      uploadedFiles,
      messageLocation,
      shouldScrollTL,
      suggestionsShown,
      selectedSuggestion,
      setMessages,
      clearFiles,
      scrollToView,
    ],
  );

  const onFileDelete = useCallback(
    (data: CallbackBusType<string>) => {
      const { payload } = data;

      setMessages((old) =>
        old.map((el) => ({
          ...el,
          files: el.files.filter(
            ({ chatSessionFileId }) => chatSessionFileId !== payload,
          ),
        })),
      );
    },
    [setMessages],
  );

  useBusEvent(StandardEventTypes.FILE_DELETE, onFileDelete);

  const updateDeletedMessage = useCallback(
    (messageId: string) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m._id !== messageId) return m;

          m.files.forEach((file) =>
            bus.broadcastEvent(StandardEventTypes.FILE_DELETED_WITH_MESSAGE, {
              chatSessionFileId: file.chatSessionFileId,
            }),
          );

          return { ...m, isDeleted: true, text: '', files: [] };
        }),
      );
    },
    [setMessages],
  );

  const onDeleteMessageRequest = useCallback(
    async (messageId: string) => {
      api.messages.deleteMessage(messageId);
      updateDeletedMessage(messageId);
    },
    [updateDeletedMessage],
  );

  const onDeleteMessageEvent = useCallback(
    ({ sessionId: payloadSessionId, messageId }: MessageDeletedEventData) => {
      if (sessionId !== payloadSessionId) return;

      updateDeletedMessage(messageId);
    },
    [sessionId, updateDeletedMessage],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_DELETED, onDeleteMessageEvent);

  const onSeenMessageEvent = useCallback(
    ({
      sessionId: payloadSessionId,
      messageId,
      seenBy,
    }: MessageSeenEventData) => {
      if (sessionId !== payloadSessionId) return;

      setMessages((prev) =>
        prev.map((m) => (m._id === messageId ? { ...m, seenBy } : m)),
      );
    },
    [sessionId, setMessages],
  );

  const updateMessage = useCallback(
    (message: Partial<MessageType>) =>
      setMessages((prev) =>
        prev.map((m) => (message._id === m._id ? { ...m, ...message } : m)),
      ),
    [setMessages],
  );

  useSocketEvent(LISTEN_EVENTS.MESSAGE_SEEN, onSeenMessageEvent);

  return (
    <div className={classes} {...restRootProps}>
      <input {...getInputProps()} />
      {showUserData && (
        <div className="chat__user-data">
          {showButtons && (
            <Button
              variant="icon"
              className="chat-arrow"
              icon={ArrowleftIcon}
              onClick={() => setContactsOpen((old) => !old)}
              aria-label="Show all chats."
            />
          )}

          <div className="chat__user-data__info">
            {loadingSession || !currentSession ? (
              <UserPhotoPlaceholder />
            ) : (
              <>
                <div className="chat__user-data__image">
                  <AvatarWithInitials
                    size={SIZE.BIG}
                    {...(isDirectMessage
                      ? { user: interlocutor }
                      : {
                          initials: getMemberInitials(
                            representerName || dealName,
                          ),
                          imageUrl: representerLogo || dealLogo,
                        })}
                  />
                </div>
                <p className="chat__users-data__name">
                  {representerName || dealName}
                </p>
              </>
            )}
          </div>

          {showButtons && (
            <Button
              variant="icon"
              className="chat-arrow"
              icon={ArrowrightIcon}
              onClick={() => setContactOpen((old) => !old)}
              aria-label="Show contact details."
            />
          )}
        </div>
      )}

      {bannerText && (
        <WarningBanner title={bannerText} icon={<LockclosedIcon />} />
      )}

      <InfiniteScroll
        ref={messagesContainerRef}
        className="chat__messages"
        localScroll
        onScroll={onMessagesScroll}
      >
        {isSessionReady ? (
          getIsArrayEmpty(finalMessages) ? (
            <MessagesEmptyState
              text={t('emptyStateChat', {
                name: representerName,
              })}
              description={
                hasRecipientWithoutAccount
                  ? `${t('leadHasNotAccountYet')} \n ${t(
                      'leadWillSeeMessageAfterSigningUp',
                    )}`
                  : t('sendFirstMessage')
              }
              imageSrc={MessagesEmptyImage}
            />
          ) : (
            finalMessages.map(({ _id, ...message }, index) => (
              <ChatMessage
                key={_id}
                messageId={_id}
                disabled={forbidMessaging}
                prevMessage={index > 0 ? finalMessages[index - 1] : null}
                onDeleteMessageRequest={onDeleteMessageRequest}
                updateMessage={updateMessage}
                {...message}
              />
            ))
          )
        ) : null}

        {loadingSpinner}
      </InfiniteScroll>

      {canManageReleaseOrder && (
        <ReleaseOrderPausedBanner
          leadId={leadId}
          isEnabled={canManageReleaseOrder}
          onWithdraw={() => openWithdrawModal(getSessionLead(currentSession))}
          location={PAUSED_LEAD_LOCATION.MESSAGING}
          mixpanelProps={getPausedLeadMixpanelProps(
            getSessionLead(currentSession),
            currentSession?.deal,
          )}
        />
      )}

      {withdrawLeadModal}

      <MessageForm
        focusTrigger={focusTrigger ?? currentSession?._id}
        disabled={forbidMessaging}
        placeholder={forbidMessagingText || t('writeAMessage')}
        resetMessage={sessionId}
        uploadFileHintMessage={t('hintYouCanDragFilesToChat')}
        buttonText={t('send')}
        files={files}
        areAllFilesProcessed={areAllFilesProcessed}
        onFileUpload={onFileUpload}
        onFileRemove={onFileRemove}
        handleOnSubmit={handleOnSendMessage}
        replySuggestions={replySuggestions}
        loadingReplySuggestions={loadingReplySuggestions}
        onReplySuggestionsRemove={removeReplySuggestions}
        onReplySuggestionSelect={selectReplySuggestion}
      />
    </div>
  );
};

export default Chat;
